import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { DayPanelEntry } from "@jackedlogs/shared";

import type { PlanStyles } from "./planComposerStyles";

// One collapsed exercise row in the edit-in-place list (shared by single
// Plan + program). Renders the member name(s) + per-member summary line
// (ramps already compressed in DayPanelEntry) + a ▸/▾ affordance. Selected
// (wide detail pane open) tints brand100 AND shows ▾ — never colour alone.
// A non-"composed" entry is an advanced (multi-variant) slot: shown
// verbatim, not editable here.
export function ComposerEntryRow({
  entry,
  idx,
  selected,
  expanded,
  onPress,
  resolveName,
  ps,
}: {
  entry: DayPanelEntry;
  idx: number;
  selected: boolean;
  expanded: boolean;
  onPress: (idx: number) => void;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  ps: PlanStyles;
}) {
  const { t } = useTranslation();

  if (entry.kind !== "composed") {
    return (
      <View style={ps.entryRow}>
        <Text style={ps.memberName} numberOfLines={1}>
          {resolveName(entry.exerciseId, entry.exerciseName)}
        </Text>
        <Text style={ps.verbatimNote}>
          {t("composer.verbatim", {
            defaultValue:
              "Advanced slot (multi-variant) — preserved as-is, not editable here",
          })}
        </Text>
      </View>
    );
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, expanded }}
      onPress={() => onPress(idx)}
      style={({ pressed }) => [
        ps.entryRow,
        selected && ps.entryRowSelected,
        pressed && ps.entryRowPressed,
      ]}
    >
      <View style={ps.entryHeadLine}>
        <View style={{ flex: 1, gap: 4 }}>
          {entry.grouped ? (
            <Text style={ps.supersetTag}>
              {t("plans.supersetTag", { defaultValue: "SUPERSET" })}
            </Text>
          ) : null}
          {entry.members.map((m, i) => (
            <View key={`${m.exerciseId}_${i}`} style={ps.memberLine}>
              {entry.grouped ? (
                <Text style={ps.memberOrdinal}>{`A${i + 1}`}</Text>
              ) : null}
              <Text style={ps.memberName} numberOfLines={1}>
                {resolveName(m.exerciseId, m.exerciseName)}
              </Text>
              {m.summary ? (
                <Text style={ps.memberSummary}>{m.summary}</Text>
              ) : null}
            </View>
          ))}
        </View>
        <Text style={ps.entryChevron}>{selected ? "▾" : "▸"}</Text>
      </View>
    </Pressable>
  );
}
